import { type FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import { IntroContext } from '@components/Intro/Intro.tsx';
import cn from 'classnames';

import {
  useBoostPlantAdvertisingMutation,
  useBoostPlantMutation,
} from '@/api/queries/boost';
import { useIntroState } from '@/common/hooks/intro-ref';
import { playSound } from '@/common/lib/soundEffect';
import { timeStringFormat } from '@/common/lib/timer';
import { type PlantType } from '@/common/types/common';
import { type BoostRequestType } from '@/common/types/query/boost';
import { ModalContext } from '@/components/Modals/Modal/config';
import { StringSmall } from '@/components/Typography/Typography';
import { MainSmButton } from '@/components/ui/Button/Button';
import { SecondaryTimer } from '@/components/ui/Timer/SecondaryTimer';
import { BoostFirebaseEventsContext } from '@/context/BoostFirebaseEventContext';

interface FreeBoostButtonProps {
  plantType: PlantType;
  effectMultiplier: number;
  duration?: number;
  nextUseInSeconds?: number;
}

const FreeBoostButton: FC<FreeBoostButtonProps> = ({
  plantType,
  effectMultiplier,
  duration,
  nextUseInSeconds,
}) => {
  const ref = useHighlightComponentRect<HTMLButtonElement>(15);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { onBoostSuccessEvent } = useContext(BoostFirebaseEventsContext);
  const { closeModal } = useContext(ModalContext);
  const { registerStepCallback } = useContext(IntroContext);
  const step12 = useIntroState(12);
  const [boostPlant] = useBoostPlantMutation({
    fixedCacheKey: 'shared-boost-mutation-request',
  });

  const [, adsBoostMutationState] = useBoostPlantAdvertisingMutation({
    fixedCacheKey: 'shared-ads-boost-mutation-request',
  });

  useEffect(() => {
    registerStepCallback(16, () => {
      closeModal();
    });
  }, []);

  const reciveFreeBoost = (): void => {
    setIsLoading(true);
    playSound('buttons/boost');
    const body: BoostRequestType = { plantType, boostType: 'Free' };
    boostPlant(body)
      .unwrap()
      .then(() => {
        onBoostSuccessEvent('boost_free_activated', {
          duration: duration as number,
          multiplier: effectMultiplier,
        });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <MainSmButton
      ref={ref}
      id="intro-step-15"
      disabled={
        isLoading || adsBoostMutationState.isLoading || !!nextUseInSeconds
      }
      isLoading={isLoading}
      inactive={!!nextUseInSeconds}
      onClick={reciveFreeBoost}
      className={cn(
        step12.showAnimation && 'relative z-[11] pointer-events-auto',
      )}
      text={''}
    >
      {nextUseInSeconds ? (
        <StringSmall className="flex gap-[5px] !text-[0.875rem] items-center justify-center font-[500] !text-white/40">
          {t('Next in')}
          <SecondaryTimer
            className="bg-transparent p-0"
            value={timeStringFormat(nextUseInSeconds * 1000)}
            variant="boostX5X10"
          />
        </StringSmall>
      ) : (
        <StringSmall weight="bold">{t('Free')}</StringSmall>
      )}
    </MainSmButton>
  );
};

export { FreeBoostButton };
